// ============================================
// Line Chart Component (using Recharts)
// ============================================

import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { colors, getSeriesColor } from '../../utils/colors';
import { compactNumber, formatDate } from '../../utils/formatters';

interface LineChartProps {
  data: any[];
  lines: {
    dataKey: string;
    name: string;
    color?: string;
    type?: 'line' | 'area';
  }[];
  xAxisKey?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  formatXAxis?: (value: string) => string;
  formatYAxis?: (value: number) => string;
  formatTooltip?: (value: number, name: string) => string;
}

export function LineChart({
  data,
  lines,
  xAxisKey = 'date',
  height = 300,
  showGrid = true,
  showLegend = true,
  formatXAxis,
  formatYAxis = compactNumber,
  formatTooltip,
}: LineChartProps) {
  const hasArea = lines.some((l) => l.type === 'area');

  const ChartComponent = hasArea ? ComposedChart : RechartsLineChart;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartComponent data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={colors.border.light} />}
        <XAxis
          dataKey={xAxisKey}
          tick={{ fontSize: 12, fill: colors.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: colors.border.light }}
          tickFormatter={formatXAxis || ((value) => {
            // Format date string to short format
            if (typeof value === 'string' && value.includes('-')) {
              const date = new Date(value);
              return `${date.getDate()}/${date.getMonth() + 1}`;
            }
            return value;
          })}
        />
        <YAxis
          tick={{ fontSize: 12, fill: colors.text.secondary }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatYAxis}
          width={60}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: `1px solid ${colors.border.light}`,
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
          formatter={(value: number, name: string) => [
            formatTooltip ? formatTooltip(value, name) : compactNumber(value),
            name,
          ]}
          labelFormatter={(label) => {
            if (typeof label === 'string' && label.includes('-')) {
              return formatDate(label);
            }
            return label;
          }}
        />
        {showLegend && (
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
            iconSize={8}
          />
        )}
        {lines.map((line, index) => {
          const lineColor = line.color || getSeriesColor(index);

          if (line.type === 'area') {
            return (
              <Area
                key={line.dataKey}
                type="monotone"
                dataKey={line.dataKey}
                name={line.name}
                stroke={lineColor}
                fill={lineColor}
                fillOpacity={0.1}
                strokeWidth={2}
              />
            );
          }

          return (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={lineColor}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, fill: 'white' }}
            />
          );
        })}
      </ChartComponent>
    </ResponsiveContainer>
  );
}

// ============================================
// Sparkline (Mini Line Chart)
// ============================================

interface SparklineProps {
  data: number[];
  color?: string;
  height?: number;
}

export function Sparkline({ data, color = colors.series[0], height = 32 }: SparklineProps) {
  const chartData = data.map((value, index) => ({ value, index }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsLineChart data={chartData}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          dot={false}
        />
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}
